/**
 * Kalkulator białka DNŻ: czysta logika obliczeń.
 *
 * Zero DOM, zero zależności. Ten sam plik działa w przeglądarce
 * (window.DnnzProteinLogic) i w Node (module.exports), więc testy
 * sprawdzają dokładnie ten kod, który liczy na stronie.
 *
 * Wszystkie liczby (współczynniki, progi, limity, pule produktów) przychodzą
 * z konfiguracji inc/data/protein-calculator.json. W tym pliku nie ma żadnej
 * wartości merytorycznej.
 *
 * Reguły: brief 3.1 do 3.4 i 3.7 z decyzjami D1 do D10 z testy/ZMIANY-COPY.md.
 * Komunikatów warunkowych nie ma (D4).
 */
(function (root, factory) {
	'use strict';
	var api = factory();
	// Oba wyjścia naraz: przy budowaniu webpackiem istnieje "module", a skrypt
	// bloku i tak szuka logiki w window, więc globalna nazwa musi powstać zawsze.
	if (typeof module === 'object' && module.exports) {
		module.exports = api;
	}
	if (root) {
		root.DnnzProteinLogic = api;
	}
}(typeof window !== 'undefined' ? window : null, function () {
	'use strict';

	// --- Liczby ---------------------------------------------------------

	/**
	 * Zaokrąglenie połówek w górę (jak Math.round w JS i round() w PHP dla
	 * liczb dodatnich). toFixed(6) ucina śmieci zmiennoprzecinkowe, bo inaczej
	 * 1,2 × 1,1 daje 1,3200000000000003, a 30,5 mogłoby spaść do 30.
	 */
	function roundHalfUp(value, decimals) {
		var factor = Math.pow(10, decimals || 0);
		return Math.round(Number((value * factor).toFixed(6))) / factor;
	}

	/**
	 * Liczba w polskim zapisie: przecinek dziesiętny, bez zbędnych zer.
	 * 1.5 -> "1,5", 1.32 -> "1,32", 2.4 -> "2,4", 105 -> "105".
	 */
	function formatDecimal(value, decimals) {
		var fixed = roundHalfUp(value, typeof decimals === 'number' ? decimals : 2).toFixed(typeof decimals === 'number' ? decimals : 2);
		if (fixed.indexOf('.') > -1) {
			fixed = fixed.replace(/0+$/, '').replace(/\.$/, '');
		}
		return fixed.replace('.', ',');
	}

	/**
	 * Odczyt wpisanej wartości. Akceptuje polski przecinek i spacje na brzegach.
	 * Zwraca liczbę, null dla pustego pola albo NaN dla tekstu, który liczbą nie jest.
	 */
	function parseNumber(raw) {
		if (raw === null || typeof raw === 'undefined') {
			return null;
		}
		var text = String(raw).trim();
		if (text === '') {
			return null;
		}
		if (!/^\d+(?:[.,]\d+)?$/.test(text)) {
			return NaN;
		}
		return Number(text.replace(',', '.'));
	}

	/**
	 * Pole z zakresem: { value, empty, invalid }. Poza zakresem = invalid.
	 */
	function readField(raw, limit) {
		var value = parseNumber(raw);
		if (value === null) {
			return { value: null, empty: true, invalid: false };
		}
		if (isNaN(value) || value < limit.min || value > limit.max) {
			return { value: null, empty: false, invalid: true };
		}
		return { value: value, empty: false, invalid: false };
	}

	// --- Wzór -------------------------------------------------------------

	/**
	 * Masa do wyliczenia (D1). Bez wzrostu = masa aktualna. Ze wzrostem:
	 * masa referencyjna = BMI 25 × wzrost², a gdy masa aktualna ją przekracza,
	 * liczymy masę referencyjną + 0,33 nadwyżki. Wartość pełna idzie do obliczeń,
	 * zaokrąglona tylko do wyświetlenia (D10).
	 */
	function adjustedWeight(weight, heightCm, cfg) {
		if (heightCm === null || typeof heightCm === 'undefined') {
			return { value: weight, adjusted: false };
		}
		var meters = heightCm / 100;
		var reference = cfg.adjusted_weight.bmi_reference * meters * meters;

		if (weight > reference) {
			return {
				value: reference + cfg.adjusted_weight.excess_share * (weight - reference),
				adjusted: true
			};
		}
		return { value: weight, adjusted: false };
	}

	/**
	 * Współczynnik końcowy g/kg. Kolejność (brief 3.3, D10): tabela 3.2,
	 * podłoga dla wieku 65+, mnożnik diety roślinnej, sufit. Po każdej
	 * korekcie zaokrąglenie do 0,01.
	 */
	function coefficient(input, cfg) {
		var c = cfg.corrections;
		var decimals = c.coefficient_decimals;
		var base = cfg.coefficients[input.activity][input.goal];
		var value = base;
		var seniorApplied = false;
		var plantApplied = false;
		var ceilingApplied = false;

		if (input.age !== null && input.age >= c.senior_age && value < c.senior_floor) {
			value = roundHalfUp(c.senior_floor, decimals);
			seniorApplied = true;
		}
		if (input.plant) {
			value = roundHalfUp(value * c.plant_multiplier, decimals);
			plantApplied = true;
		}
		if (value > c.ceiling) {
			value = roundHalfUp(c.ceiling, decimals);
			ceilingApplied = true;
		}

		return {
			value: roundHalfUp(value, decimals),
			base: base,
			seniorApplied: seniorApplied,
			plantApplied: plantApplied,
			ceilingApplied: ceilingApplied
		};
	}

	/**
	 * Wynik stanu B (brief 3.4, 3.7). Zakres górny nie przekracza sufitu.
	 * Porcja na posiłek od wyświetlanej liczby głównej (D10).
	 */
	function compute(input, cfg) {
		var c = cfg.corrections;
		var weight = adjustedWeight(input.weight, input.height, cfg);
		var k = coefficient(input, cfg);
		var protein = roundHalfUp(weight.value * k.value);

		return {
			weightForCalc: weight.value,
			weightDisplay: roundHalfUp(weight.value),
			adjusted: weight.adjusted,
			coefficient: k.value,
			coefficientInfo: k,
			protein: protein,
			rangeLow: roundHalfUp(weight.value * (k.value - c.range_delta)),
			rangeHigh: roundHalfUp(weight.value * Math.min(k.value + c.range_delta, c.ceiling)),
			meals: input.meals,
			perMeal: roundHalfUp(protein / input.meals)
		};
	}

	/**
	 * Tabelka stanu A (D8): 60/70/80/90 kg z konfiguracji. Reaguje na cel,
	 * aktywność, wiek i dietę roślinną. Wzrostu nie bierze pod uwagę.
	 */
	function samples(input, cfg) {
		var k = coefficient(input, cfg);
		return {
			coefficient: k.value,
			ageChanged: k.seniorApplied,
			plant: !!input.plant,
			rows: cfg.sample_weights.map(function (weight) {
				return { weight: weight, protein: roundHalfUp(weight * k.value) };
			})
		};
	}

	// --- Teksty z szablonów ------------------------------------------------

	/**
	 * Podstawienie {klucz} w szablonie. Teksty żyją w szablonie Twig
	 * (atrybuty data-*), skrypt tylko wstawia liczby.
	 */
	function fill(template, values) {
		return String(template).replace(/\{([a-z_0-9]+)\}/g, function (match, key) {
			return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match;
		});
	}

	function findByKey(list, key) {
		for (var i = 0; i < list.length; i++) {
			if (list[i].key === key) {
				return list[i];
			}
		}
		return null;
	}

	/**
	 * Podpis pod tabelką stanu A. Wiek dopisujemy tylko wtedy, gdy zmienił
	 * współczynnik, dietę roślinną tylko wtedy, gdy jest włączona.
	 */
	function samplesCaption(input, sampleInfo, templates, cfg) {
		var text = fill(templates.base, {
			cel: findByKey(cfg.goals, input.goal).caption,
			poziom: findByKey(cfg.activities, input.activity).caption
		});
		if (sampleInfo.ageChanged) {
			text += fill(templates.age, { wiek: input.age });
		}
		if (sampleInfo.plant) {
			text += templates.plant;
		}
		return text + templates.end;
	}

	// --- Przykłady produktów (5.3, D6) --------------------------------------

	function findRow(cfg, id) {
		for (var g = 0; g < cfg.table.length; g++) {
			var rows = cfg.table[g].rows;
			for (var r = 0; r < rows.length; r++) {
				if (rows[r].id === id) {
					return rows[r];
				}
			}
		}
		return null;
	}

	/**
	 * Pula przykładów z białkiem i adresem dociągniętymi z tabeli B07,
	 * żeby wartość na 100 g była w konfiguracji tylko raz.
	 */
	function pool(cfg, plant) {
		return cfg.examples[plant ? 'plant' : 'animal'].map(function (item) {
			var row = findRow(cfg, item.row);
			return {
				id: item.row,
				genitive: item.genitive,
				maxGrams: item.max_grams,
				protein100g: row.protein_100g,
				url: row.url
			};
		});
	}

	/**
	 * Gramatura przykładu (brief 5.3): round(na_posilek / białko_100 × 100 / 5) × 5.
	 */
	function exampleGrams(perMeal, protein100g, cfg) {
		var step = cfg.examples.grams_step;
		return roundHalfUp((perMeal / protein100g) * 100 / step) * step;
	}

	/**
	 * Pierwsze pozycje z ustalonej kolejności, które mieszczą się w limitach
	 * (minimum z briefu, maksimum per produkt z D6). Mniej niż 3 = tyle, ile jest.
	 */
	function pickExamples(perMeal, order, items, cfg) {
		var picked = [];
		for (var i = 0; i < order.length && picked.length < cfg.examples.count; i++) {
			var item = items[order[i]];
			var grams = exampleGrams(perMeal, item.protein100g, cfg);
			if (grams >= cfg.examples.grams_min && grams <= item.maxGrams) {
				picked.push({ id: item.id, grams: grams, genitive: item.genitive, url: item.url });
			}
		}
		return picked;
	}

	/**
	 * Generator liczb pseudolosowych z ziarnem (mulberry32). Na stronie ziarno
	 * pochodzi z Math.random(), w testach jest stałe.
	 */
	function createRng(seed) {
		var state = seed >>> 0;
		return function () {
			state = (state + 0x6D2B79F5) >>> 0;
			var t = state;
			t = Math.imul(t ^ (t >>> 15), t | 1);
			t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
			return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
		};
	}

	/** Losowa kolejność indeksów 0..length-1 (Fisher-Yates). */
	function shuffledOrder(length, rng) {
		var order = [];
		for (var i = 0; i < length; i++) {
			order.push(i);
		}
		for (var j = length - 1; j > 0; j--) {
			var k = Math.floor(rng() * (j + 1));
			var tmp = order[j];
			order[j] = order[k];
			order[k] = tmp;
		}
		return order;
	}

	/**
	 * Losowanie przykładów (D6): obie pule dostają losową kolejność raz, przy
	 * wejściu na stronę. Zmiana pól liczy tylko gramatury w tej samej kolejności.
	 * Nową kolejność dostaje pula dopiero przy przełączeniu diety roślinnej.
	 */
	function createExamplePicker(cfg, rng) {
		var pools = { animal: pool(cfg, false), plant: pool(cfg, true) };
		var orders = {
			animal: shuffledOrder(pools.animal.length, rng),
			plant: shuffledOrder(pools.plant.length, rng)
		};
		var lastPlant = null;

		function key(plant) {
			return plant ? 'plant' : 'animal';
		}

		return {
			pick: function (perMeal, plant) {
				plant = !!plant;
				if (lastPlant !== null && plant !== lastPlant) {
					orders[key(plant)] = shuffledOrder(pools[key(plant)].length, rng);
				}
				lastPlant = plant;
				return pickExamples(perMeal, orders[key(plant)], pools[key(plant)], cfg);
			},
			order: function (plant) {
				return orders[key(plant)].slice();
			}
		};
	}

	/**
	 * Linia doprecyzowania stanu B jako lista odcinków: tekst albo link.
	 * Szablony z Twiga: base ("Przy {posilki} posiłkach to ok. {na_posilek} g
	 * na posiłek"), lead (", np. "), item ("{g} g "), comma (", "), or (" albo "),
	 * end ("."). Przy dwóch pozycjach "albo" między nimi, przy jednej sama
	 * pozycja, przy zerze linia bez części "np." (D6).
	 */
	function mealLine(result, examples, templates) {
		var parts = [{ text: fill(templates.base, { posilki: result.meals, na_posilek: result.perMeal }) }];

		if (examples.length) {
			parts.push({ text: templates.lead });
			examples.forEach(function (example, index) {
				if (index > 0) {
					parts.push({ text: index === examples.length - 1 ? templates.or : templates.comma });
				}
				parts.push({ text: fill(templates.item, { g: example.grams }) });
				parts.push({ link: { href: example.url, text: example.genitive } });
			});
		}

		parts.push({ text: templates.end });
		return parts;
	}

	/** Linia jako czysty tekst (do testów i do komunikatu dla czytnika ekranu). */
	function partsToText(parts) {
		return parts.map(function (part) {
			return part.link ? part.link.text : part.text;
		}).join('');
	}

	// --- Stan całego formularza ---------------------------------------------

	/**
	 * Ocena formularza. raw: wartości pól tekstowych (masa, wiek, wzrost)
	 * i wybory (cel, poziom, posiłki, dieta roślinna). lastAge: ostatni poprawny
	 * wiek, żeby tabelka stanu A nie gubiła wartości, gdy pole wieku jest
	 * chwilowo puste albo błędne (tak jak czas w spalaniu).
	 *
	 * Stan B tylko przy poprawnej masie, poprawnym wieku i poprawnym albo
	 * pustym wzroście. Wartość wpisana spoza zakresu = komunikat przy polu
	 * i stan A. Puste pole nie daje komunikatu (D9): pusta masa albo pusty
	 * wiek = stan A, pusty wzrost = liczymy bez korekty masy.
	 */
	function evaluate(raw, cfg, lastAge) {
		var limits = cfg.limits;
		var weight = readField(raw.weight, limits.weight);
		var age = readField(raw.age, limits.age);
		var height = readField(raw.height, limits.height);

		var sampleAge = age.value !== null ? age.value : (typeof lastAge === 'number' ? lastAge : limits.age.default);
		var choices = { goal: raw.goal, activity: raw.activity, plant: !!raw.plant };

		var evaluation = {
			errors: {
				weight: weight.invalid,
				age: age.invalid,
				height: height.invalid
			},
			sampleAge: sampleAge,
			samples: samples({ goal: choices.goal, activity: choices.activity, plant: choices.plant, age: sampleAge }, cfg),
			state: 'A',
			result: null
		};

		if (weight.value !== null && age.value !== null && !height.invalid) {
			evaluation.state = 'B';
			evaluation.result = compute({
				weight: weight.value,
				height: height.value,
				age: age.value,
				goal: choices.goal,
				activity: choices.activity,
				meals: Number(raw.meals),
				plant: choices.plant
			}, cfg);
		}

		return evaluation;
	}

	return {
		roundHalfUp: roundHalfUp,
		formatDecimal: formatDecimal,
		parseNumber: parseNumber,
		readField: readField,
		adjustedWeight: adjustedWeight,
		coefficient: coefficient,
		compute: compute,
		samples: samples,
		fill: fill,
		samplesCaption: samplesCaption,
		pool: pool,
		exampleGrams: exampleGrams,
		pickExamples: pickExamples,
		createRng: createRng,
		shuffledOrder: shuffledOrder,
		createExamplePicker: createExamplePicker,
		mealLine: mealLine,
		partsToText: partsToText,
		evaluate: evaluate
	};
}));
